import type { ReactNode } from 'react'
import type {
  LocalizedSpeechPreviewItem,
  LocalizedSpeechPreviewResponseData,
} from '@/shared/api/types/localized-speech-types'
import type { TranslationJobScope } from '@/shared/constants/status'
import type { LocalizedEditorFieldConfig } from '@/shared/ui/localized-fields-editor'
import camelcase from 'camelcase'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocalizedTranslationJobController } from '@/features/localized-translation/hooks/use-localized-translation-job-controller'
import { useLanguageLabel } from '@/shared/hooks/use-language-label'
import { showToast } from '@/shared/libs/toast'
import { Button } from '@/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog'
import { LocalizedFieldsEditor } from '@/shared/ui/localized-fields-editor'

export interface NameDescriptionTranslation {
  language: string
  name: string
  description: string
  title?: string
}

type TranslationFieldKey = 'title' | 'name' | 'description' | 'audioContent'

interface RenderExtraContentContext<TTranslation extends NameDescriptionTranslation> {
  /** Bản dịch của ngôn ngữ đang được chọn, dùng cho UI riêng theo domain như audio của POI. */
  currentTranslation: TTranslation
  /** Cập nhật một phần dữ liệu cho ngôn ngữ đang chọn và đẩy thay đổi qua onChange. */
  updateCurrent: (patch: Partial<TTranslation>) => void
  /** Mã ngôn ngữ đang được chọn trong editor. */
  activeLanguage: string
  /** Cho biết ngôn ngữ đang chọn bị khóa do readonly hoặc do job dịch đang chạy. */
  isReadOnly: boolean
}

export interface LocalizedTranslationJobSectionProps<
  TTranslation extends NameDescriptionTranslation,
> {
  /** Danh sách ngôn ngữ phải hiển thị trong editor; row thiếu sẽ được tự tạo với name/description rỗng. */
  languages: string[]
  /** Giá trị bản dịch hiện tại do form cha quản lý. */
  translations: TTranslation[]
  /** Hàm trả về danh sách bản dịch mới sau khi user sửa hoặc sau khi áp dụng kết quả job. */
  onChange: (translations: TTranslation[]) => void
  /** Scope dùng để lọc job dịch đúng loại entity hiện tại, ví dụ POI/Tour/Destination. */
  scope: TranslationJobScope
  /** Id ổn định dùng để job store ghép kết quả dịch về đúng form hiện tại. */
  entityId: string
  /** Nhãn tùy chọn hiển thị trong toast hoặc background jobs panel. */
  entityLabel?: string
  /** Đường dẫn tùy chọn để mở lại entity từ background jobs panel. */
  entityPath?: string
  /** Bật/tắt tạo audio khi gọi preview API. */
  audioEnabled: boolean
  /** Field dùng để tạo speech/audio khi audioEnabled=true. Mặc định là description. */
  speechField?: TranslationFieldKey | string
  /** Thông báo lỗi khi ngôn ngữ nguồn chưa đủ dữ liệu để dịch. */
  requiredMessage: string
  /** Hàm tạo cấu hình field theo active language để placeholder có thể đổi theo ngôn ngữ. */
  fields: (activeLanguage: string) => LocalizedEditorFieldConfig<TranslationFieldKey>[]
  /** Nhãn tùy chọn cho nút dịch. */
  translateLabel?: string
  /** Mô tả tùy chọn ở panel chọn ngôn ngữ đích. */
  scopeHint?: string
  /** Tiêu đề tùy chọn của block editor, truyền xuống LocalizedFieldsEditor. */
  blockTitle?: string
  /** Nội dung loading tùy chọn, giữ API component thống nhất với các flow dịch trực tiếp. */
  loadingText?: string
  /** Khóa field nhập liệu nhưng vẫn cho phép xem/chuyển tab ngôn ngữ. */
  readonly?: boolean
  /** Báo cho form cha biết còn thay đổi cần tạo lại dịch/audio trước khi lưu hay không. */
  onGenerationStateChange?: (hasPendingGeneration: boolean) => void
  /** Ngôn ngữ nên được mở sẵn trong editor khi form được vào từ filter/list. */
  initialActiveLanguage?: string
  /** Cho phép domain tự merge dữ liệu preview đặc thù, ví dụ audioUrl/hasAudio của POI. */
  applyPreviewToTranslation?: (
    translation: TTranslation,
    preview: LocalizedSpeechPreviewItem,
  ) => TTranslation
  /** Render UI riêng của domain bên dưới editor mà không phải copy lại workflow dịch. */
  renderExtraContent?: (context: RenderExtraContentContext<TTranslation>) => ReactNode
}

/**
 * Lấy phần preview đúng ngôn ngữ từ response API, có xử lý key dạng camelCase.
 */
function getTranslatedPreviewByLanguage(
  data: LocalizedSpeechPreviewResponseData,
  language: string,
) {
  return data[camelcase(language)]
}

/**
 * Gộp dữ liệu mặc định cho các entity chỉ có name/description, ví dụ Tour và Destination.
 */
function applyNameDescriptionPreview<TTranslation extends NameDescriptionTranslation>(
  translation: TTranslation,
  preview: LocalizedSpeechPreviewItem,
) {
  return {
    ...translation,
    ...(preview.fields['title'] !== undefined ? { title: preview.fields['title'] } : {}),
    name: preview.fields['name'] ?? translation.name,
    description: preview.fields['description'] ?? translation.description,
  } as TTranslation
}

/**
 * Component dùng chung cho workflow dịch nền của các entity có localized name/description.
 */
export function LocalizedTranslationJobSection<TTranslation extends NameDescriptionTranslation>({
  languages,
  translations,
  onChange,
  scope,
  entityId,
  entityLabel,
  entityPath,
  audioEnabled,
  speechField,
  requiredMessage,
  fields,
  translateLabel,
  scopeHint,
  blockTitle,
  loadingText,
  readonly = false,
  onGenerationStateChange,
  initialActiveLanguage,
  applyPreviewToTranslation,
  renderExtraContent,
}: LocalizedTranslationJobSectionProps<TTranslation>) {
  const { t } = useTranslation()
  const languageLabel = useLanguageLabel()
  const { runningJobs, pendingSuccessJobs, enqueueTranslationJob, consumeJob, consumeJobRestore } =
    useLocalizedTranslationJobController({
      scope,
      entityId,
      ...(entityLabel ? { entityLabel } : {}),
      ...(entityPath ? { entityPath } : {}),
      audioEnabled,
      ...(speechField ? { speechField } : {}),
    })
  const [activeLanguage, setActiveLanguage] = useState<string>(
    initialActiveLanguage ?? languages[0] ?? '',
  )
  const [dirtyLanguages, setDirtyLanguages] = useState<Record<string, boolean>>({})
  const [openTranslateConfirm, setOpenTranslateConfirm] = useState(false)
  const [selectedOutputLanguages, setSelectedOutputLanguages] = useState<string[]>([])

  const normalized = useMemo(
    () =>
      languages.map((language) => {
        const existing = translations.find((item) => item.language === language)
        return existing ?? ({ language, name: '', description: '' } as TTranslation)
      }),
    [languages, translations],
  )
  const latestTranslationsRef = useRef(normalized)
  const appliedInitialActiveLanguageRef = useRef<string | null>(null)
  const appliedRunningResultSignaturesRef = useRef(new Map<string, string>())
  const restoredRunningJobIdsRef = useRef(new Set<string>())
  const [lockedRunningJobId, setLockedRunningJobId] = useState<string | null>(null)

  useEffect(() => {
    /** Giữ snapshot mới nhất để job async không apply vào state cũ. */
    latestTranslationsRef.current = normalized
  }, [normalized])

  const resolvedActiveLanguage = useMemo(() => {
    if (normalized.some((item) => item.language === activeLanguage)) {
      return activeLanguage
    }
    return normalized[0]?.language ?? ''
  }, [activeLanguage, normalized])

  useEffect(() => {
    if (
      !initialActiveLanguage ||
      appliedInitialActiveLanguageRef.current === initialActiveLanguage
    ) {
      return
    }
    if (!languages.includes(initialActiveLanguage)) {
      return
    }
    appliedInitialActiveLanguageRef.current = initialActiveLanguage
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot sync of controlled prop into local state, guarded by ref to avoid loops
    setActiveLanguage(initialActiveLanguage)
  }, [initialActiveLanguage, languages])

  const currentTranslation = normalized.find((tr) => tr.language === resolvedActiveLanguage)
  const activeFieldConfigs = useMemo(
    () => fields(resolvedActiveLanguage),
    [fields, resolvedActiveLanguage],
  )
  const requiredFieldKeys = useMemo(
    () => activeFieldConfigs.filter((field) => field.required !== false).map((field) => field.key),
    [activeFieldConfigs],
  )
  const completedLanguages = normalized
    .filter((tr) =>
      activeFieldConfigs.some((field) => String(tr[field.key as keyof TTranslation] ?? '').trim()),
    )
    .map((tr) => tr.language)
  const allLanguages = normalized.map((item) => item.language).filter(Boolean)
  const effectiveSelectedOutputLanguages = useMemo(() => {
    /** Luôn giữ language hiện tại trong output để API trả đủ context cho source. */
    const valid = selectedOutputLanguages.filter((language) => allLanguages.includes(language))
    if (resolvedActiveLanguage && !valid.includes(resolvedActiveLanguage)) {
      valid.unshift(resolvedActiveLanguage)
    }
    return Array.from(new Set(valid))
  }, [allLanguages, resolvedActiveLanguage, selectedOutputLanguages])

  const hasPendingTextEdit = Boolean(dirtyLanguages[resolvedActiveLanguage])
  const hasPendingGeneration = Object.values(dirtyLanguages).some(Boolean)
  const isViewingRestoredRunningJob =
    lockedRunningJobId !== null && runningJobs.some((job) => job.id === lockedRunningJobId)
  const isLanguageReadOnly = readonly || isViewingRestoredRunningJob
  const valuesByLanguage = useMemo(
    () =>
      Object.fromEntries(
        normalized.map((item) => [
          item.language,
          Object.fromEntries(
            activeFieldConfigs.map((field) => [
              field.key,
              String(item[field.key as keyof TTranslation] ?? ''),
            ]),
          ),
        ]),
      ) as Record<string, Record<TranslationFieldKey, string>>,
    [normalized, activeFieldConfigs],
  )

  useEffect(() => {
    onGenerationStateChange?.(hasPendingGeneration)
  }, [hasPendingGeneration, onGenerationStateChange])

  useEffect(() => {
    /** Apply kết quả từng ngôn ngữ ngay khi SSE trả về; completed vẫn đối soát toàn bộ kết quả cuối. */
    const runningJobIds = new Set(runningJobs.map((job) => job.id))
    appliedRunningResultSignaturesRef.current.forEach((_, jobId) => {
      if (!runningJobIds.has(jobId)) {
        appliedRunningResultSignaturesRef.current.delete(jobId)
      }
    })

    if (latestTranslationsRef.current.length < 1) {
      return
    }

    runningJobs.forEach((job) => {
      const result = job.result
      if (!result) {
        return
      }

      const signature = JSON.stringify(result)
      if (appliedRunningResultSignaturesRef.current.get(job.id) === signature) {
        return
      }

      let hasChanged = false
      let hasMatchedLanguage = false
      const nextTranslations = latestTranslationsRef.current.map((item) => {
        const preview = getTranslatedPreviewByLanguage(result, item.language)
        if (!preview) {
          return item
        }
        hasMatchedLanguage = true
        const nextItem = (applyPreviewToTranslation ?? applyNameDescriptionPreview)(item, preview)
        if (nextItem === item || JSON.stringify(nextItem) === JSON.stringify(item)) {
          return item
        }
        hasChanged = true
        return nextItem
      })

      if (!hasMatchedLanguage) {
        return
      }
      appliedRunningResultSignaturesRef.current.set(job.id, signature)
      if (hasChanged) {
        latestTranslationsRef.current = nextTranslations
        onChange(nextTranslations)
      }
      setDirtyLanguages((prev) => {
        const next = { ...prev }
        Object.keys(result).forEach((language) => {
          next[language] = false
        })
        return next
      })
    })
  }, [applyPreviewToTranslation, normalized, onChange, runningJobs])

  useEffect(() => {
    /** Apply từng job thành công khi form đã có đủ rows ngôn ngữ, sau đó consume để tránh lặp side effect. */
    if (pendingSuccessJobs.length < 1) {
      return
    }
    pendingSuccessJobs.forEach((job) => {
      if (!job.result) {
        consumeJob(job.id)
        return
      }
      if (latestTranslationsRef.current.length < 1) {
        return
      }
      const mapped = job.result
      let hasChanged = false
      let hasMatchedLanguage = false
      const nextTranslations = latestTranslationsRef.current.map((item) => {
        const preview = getTranslatedPreviewByLanguage(mapped, item.language)
        if (!preview) {
          return item
        }
        hasMatchedLanguage = true
        const nextItem = (applyPreviewToTranslation ?? applyNameDescriptionPreview)(item, preview)
        if (nextItem === item || JSON.stringify(nextItem) === JSON.stringify(item)) {
          return item
        }
        hasChanged = true
        return nextItem
      })
      if (hasChanged) {
        onChange(nextTranslations)
      }
      if (!hasMatchedLanguage) {
        return
      }
      setDirtyLanguages((prev) => {
        const next = { ...prev }
        job.outputLanguages.forEach((language) => {
          next[language] = false
        })
        return next
      })
      consumeJob(job.id)
    })
  }, [applyPreviewToTranslation, consumeJob, normalized, onChange, pendingSuccessJobs])

  useEffect(() => {
    if (normalized.length < 1) {
      return
    }
    const jobWithDraft = runningJobs.find(
      (job) =>
        job.restoreRequested === true &&
        job.sourceTranslations !== undefined &&
        !restoredRunningJobIdsRef.current.has(job.id),
    )
    if (!jobWithDraft || !Array.isArray(jobWithDraft.sourceTranslations)) {
      return
    }

    const sourceTranslations = jobWithDraft.sourceTranslations as TTranslation[]
    const sourceByLanguage = new Map(sourceTranslations.map((item) => [item.language, item]))
    let hasChanged = false
    let hasMatchedLanguage = false
    const nextTranslations = normalized.map((item) => {
      const source = sourceByLanguage.get(item.language)
      if (!source) {
        return item
      }
      hasMatchedLanguage = true
      if (JSON.stringify(source) === JSON.stringify(item)) {
        return item
      }
      hasChanged = true
      return source
    })
    if (!hasMatchedLanguage) {
      return
    }
    if (!hasChanged) {
      setLockedRunningJobId(jobWithDraft.id)
      restoredRunningJobIdsRef.current.add(jobWithDraft.id)
      consumeJobRestore(jobWithDraft.id)
      return
    }
    onChange(nextTranslations)
    setLockedRunningJobId(jobWithDraft.id)
    restoredRunningJobIdsRef.current.add(jobWithDraft.id)
    consumeJobRestore(jobWithDraft.id)
  }, [consumeJobRestore, normalized, onChange, runningJobs])

  /**
   * Cập nhật một phần dữ liệu của ngôn ngữ đang chọn, dùng cho slot UI riêng theo domain.
   */
  function updateCurrent(patch: Partial<TTranslation>) {
    if (isLanguageReadOnly) {
      return
    }
    if (!resolvedActiveLanguage) {
      return
    }
    onChange(
      normalized.map((item) =>
        item.language === resolvedActiveLanguage ? ({ ...item, ...patch } as TTranslation) : item,
      ),
    )
  }

  /**
   * Validate dữ liệu nguồn và enqueue job dịch nền cho các ngôn ngữ đã chọn.
   */
  async function startTranslationJob(targetLanguages: string[]) {
    /** Mỗi entity chỉ được phép chạy 1 job dịch tại một thời điểm. */
    if (runningJobs.length >= 1) {
      showToast.error(t('poi.localizedSpeech.jobLimitReached'))
      return
    }

    /** Validate source trước khi enqueue để tránh job nền vô nghĩa. */
    const sourceFields = Object.fromEntries(
      activeFieldConfigs.map((field) => [
        field.key,
        String(currentTranslation?.[field.key as keyof TTranslation] ?? '').trim(),
      ]),
    )
    if (requiredFieldKeys.some((field) => !sourceFields[field])) {
      showToast.error(requiredMessage)
      return
    }

    const outputLanguages = targetLanguages.filter(Boolean)
    if (outputLanguages.length < 1) {
      return
    }

    enqueueTranslationJob({
      fields: sourceFields,
      inputLanguage: resolvedActiveLanguage.split('-')[0] ?? resolvedActiveLanguage,
      outputLanguages,
      sourceTranslations: normalized,
    })
  }

  if (!currentTranslation) {
    return null
  }

  return (
    <div>
      <div className="space-y-5">
        <LocalizedFieldsEditor<TranslationFieldKey>
          {...(blockTitle ? { title: blockTitle } : {})}
          languages={languages}
          activeLanguage={resolvedActiveLanguage}
          onSelectLanguage={setActiveLanguage}
          completedLanguages={completedLanguages}
          valuesByLanguage={valuesByLanguage}
          onChangeField={(language, field, value) => {
            if (isLanguageReadOnly) {
              return
            }
            onChange(
              normalized.map((item) =>
                item.language === language ? ({ ...item, [field]: value } as TTranslation) : item,
              ),
            )
            setDirtyLanguages((prev) => ({ ...prev, [language]: true }))
          }}
          fields={activeFieldConfigs}
          translatePanel={{
            visible: !isLanguageReadOnly && hasPendingTextEdit,
            languages: allLanguages,
            selectedLanguages: effectiveSelectedOutputLanguages,
            onSelectedLanguagesChange: setSelectedOutputLanguages,
            onTranslate: () => {
              if (runningJobs.length >= 1) {
                showToast.error(t('poi.localizedSpeech.jobLimitReached'))
                return
              }
              setOpenTranslateConfirm(true)
            },
            translateLabel: translateLabel ?? t('poi.localizedSpeech.generateSelectedTextOnly'),
            scopeHint: scopeHint ?? t('poi.localizedSpeech.scopeHint'),
            selectAllLabel: t('common.selectAll'),
            currentLabel: t('common.current'),
            loadingText: loadingText ?? t('poi.localizedSpeech.generatingTextOnly'),
            isTranslating: runningJobs.length >= 1,
          }}
          isFieldReadOnly={isLanguageReadOnly}
          {...(isLanguageReadOnly
            ? {
                readOnlyHint: t('common.readOnly', { defaultValue: 'Read only' }),
              }
            : {})}
        />
        {renderExtraContent?.({
          currentTranslation,
          updateCurrent,
          activeLanguage: resolvedActiveLanguage,
          isReadOnly: isLanguageReadOnly,
        })}
      </div>

      <Dialog
        open={openTranslateConfirm}
        onOpenChange={(open) => {
          if (!open) {
            setOpenTranslateConfirm(false)
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('poi.localizedSpeech.confirmSelectedTitle')}</DialogTitle>
            <DialogDescription>
              {t('poi.localizedSpeech.confirmSelectedDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3 text-sm text-zinc-700">
            {effectiveSelectedOutputLanguages.map((language) => languageLabel(language)).join(', ')}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenTranslateConfirm(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() => {
                setOpenTranslateConfirm(false)
                void startTranslationJob(effectiveSelectedOutputLanguages)
              }}
            >
              {t('common.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
